'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { Typography } from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { getPaths } from '../../common';
import { TrackingDataProvider } from '../../common/components/TrackingDataProvider';
import { useNavigate } from '../../common/hooks/useNavigate';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';
import { ProductRankingCarouselCard } from '../components';

export const SalesRankingSection = () => {
  const { data: mypageData, isLoading } = useQuery(curatorQueries.mypage.data());
  const { service } = useDeviceInfo();
  const { navigate } = useNavigate();
  const paths = getPaths(service);

  const handleClickMore = () => {
    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'my_ranking_more',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        section_name: 'mypage_ranking',
      });

      pushDataLayer('click_button', {
        brand_id: GTM_NOTSET_STRING,
        button_id: 'my_ranking_more',
        button_name: 'goods',
        index: GTM_NOTSET_STRING,
        section_index: '8',
        section_name: 'mypage_ranking',
      });
    } else {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
        data: {
          page_name: 'curator_mypage',
          section_name: 'ProductSalesRanking',
          section_title: '판매 상품 랭킹',
          button_id: 'more',
          button_name: '더보기',
          position: -1,
        },
      });
    }

    navigate(paths.salesRanking());
  };

  const salesRankingGoods = mypageData?.salesRankingGoods ?? [];
  const isEmpty = !isLoading && salesRankingGoods.length === 0;
  const is29CM = service === '29CM';

  return (
    // SID-4418: 큐레이션 판매 상품 랭킹 섹션 배경은 항상 gray-100. 빈 상태는 흰색 카드/divider
    // 없이 회색 섹션으로 표현해 위·아래 흰색 섹션과 배경 대비로 구분한다(라인 없음).
    <div className="w-full bg-gray-100">
      <div
        className={clsx(
          'px-[16px]! pb-[8px]! flex justify-between items-end gap-[8px]!',
          is29CM && isEmpty && 'pt-[20px]!',
          is29CM && !isEmpty && 'pt-[24px]!',
          !is29CM && 'pt-[12px]!',
        )}
      >
        <Typography variant="title18pxMed">큐레이션 판매 상품 랭킹</Typography>
        <Typography
          onClick={handleClickMore}
          className="cursor-pointer"
          textDecoration="underline"
          variant="body13pxReg"
          colorScheme="gray-600"
        >
          더보기
        </Typography>
      </div>

      {isLoading ? null : isEmpty ? (
        // SID-4418: 빈 상태 안내 문구만. 하단 divider 제거(회색 배경 대비로 다음 섹션과 구분).
        <div className="flex flex-col items-center justify-center gap-[12px]! px-[16px]! py-[40px]!">
          {/* Figma 1539-33630 — empty 는 단일 안내 문구(size14 medium)만. 부가문구 없음. (SID-4156) */}
          <div className="flex flex-col items-center gap-[2px]! w-full">
            <Typography size="14" weight="medium">
              이번 달 판매 상품이 없습니다.
            </Typography>
          </div>
        </div>
      ) : (
        <div className={clsx('pb-[24px]!')}>
          <TrackingDataProvider
            data={{
              page_name: 'curator_mypage',
              section_name: 'ProductSalesRanking',
              section_title: '큐레이션 판매 상품 랭킹',
            }}
          >
            <div className="overflow-x-auto">
              <div className="flex bg-white w-fit mx-[16px]! rounded-[4px] overflow-hidden">
                {salesRankingGoods.map((item, index) => (
                  <ProductRankingCarouselCard
                    key={item.linkUrl}
                    rank={index + 1}
                    imageUrl={item.imageUrl}
                    brandName={item.brandName}
                    goodsName={item.goodsName}
                    goodsNo={item.goodsNo}
                    price={item.price}
                    totalQuantity={item.totalQuantity}
                    totalCommission={item.totalCommission}
                    linkUrl={item.linkUrl}
                    discountRate={item.salesRate}
                    initialIsLiked={item.isLiked}
                  />
                ))}
              </div>
            </div>
          </TrackingDataProvider>
        </div>
      )}
    </div>
  );
};
